<template>
    <LoadingCard v-if="loading" />
    <div v-else>
        <div class="toolbar">
            <div class="toolbar__label">
                Stock on hand on {{ currentDate | date_format }}
            </div>
            <div class="actions">
                <i class="actions__item zmdi zmdi-search" @click="enableSearch"></i>
                <i class="actions__item zmdi zmdi-print" @click="print"></i>
                <i class="actions__item far fa-check-square"
                   :class="onlySelected ? 'fa-check-square' : 'fa-square'" @click="onlySelected=!onlySelected"
                   v-b-tooltip="'Show only on hand items'"></i>
            </div>

            <div class="toolbar__search" :class="searchEnabled ? 'd-block' : 'd-none'">
                <input type="text" placeholder="Search..." ref="searchInput" v-model="searchTerm">
                <i class="toolbar__search__close zmdi zmdi-long-arrow-left" @click="disableSearch"></i>
            </div>
        </div>

        <div class="card">
            <div class="card-header bg-primary text-white">Selected Commercial Chemicals</div>

            <b-table small hover :stacked="isPrint ? false : 'md'" :items="filteredData" :fields="table.fields"
                     sort-by="name" :show-empty="true">

                <template slot="activeIngredients" slot-scope="row">
                            <span class="badge-group mr-2" v-for="ingredient in [...row.item.activeIngredients].sort((a,b) => a.activeIngredient.name.localeCompare(b.activeIngredient.name))">
                                <span class="badge badge-secondary mb-1">{{ ingredient.activeIngredient.name }}</span>
                                <span class="badge badge-warning mb-1">{{ ingredient.ingredient_percentage }}%</span>
                            </span>
                </template>


                <template slot="hsr_number" slot-scope="row">
                    <a :href="row.item.hsr_link" target="_blank">
                        {{ row.item.hsr_number}}
                    </a>
                </template>

                <template slot="stock_on_hand" slot-scope="row">
                    <div class="position-relative" v-if="row.item.id in updatedValues">
                        <VueMask v-model.number="updatedValues[row.item.id]" class="form-control"
                                 placeholder="0..."
                                 :ref="`stockInput${row.item.id}`"
                                 :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}"
                                 @input="setRow(row.item, $event)" />
                        <i class="form-group__bar"></i>
                    </div>
                    <template v-else-if="row.item.stock_on_hand">{{ row.item.stock_on_hand | number_format }}</template>
                    <span class="text-muted" v-else>(none)</span>
                </template>
                <template slot="classification" slot-scope="row">
                    {{ row.item.classification.name }}
                </template>
                <template slot="actions" slot-scope="row">
                    <b-button variant="primary btn--icon btn-sm" v-if="row.item.id in updatedValues"
                              :disabled="disabledValues.includes(row.item.id)"
                              @click="saveRow(row.item)">
                        <i class="zmdi zmdi-save"></i>
                    </b-button>
                    <b-button variant="warning btn--icon btn-sm" @click="enableEditRow(row.item)" v-else>
                        <i class="zmdi zmdi-edit"></i>
                    </b-button>
                </template>
            </b-table>
        </div>

        <div class="card">
            <div class="card-header bg-primary text-white">Selected Fertilisers</div>

            <b-table small hover :stacked="isPrint ? false : 'md'" :items="filteredFertiliserData" :fields="fertiliserTable.fields"
                     sort-by="name" :show-empty="true">

                <template slot="stock_on_hand" slot-scope="row">
                    <div class="position-relative" v-if="row.item.id in updatedFertiliserValues">
                        <VueMask v-model.number="updatedFertiliserValues[row.item.id]" class="form-control"
                                 placeholder="0..."
                                 :ref="`stockInputFertiliser${row.item.id}`"
                                 :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}"
                                 @input="setFertiliserRow(row.item, $event)" />
                        <i class="form-group__bar"></i>
                    </div>
                    <template v-else-if="row.item.stock_on_hand">{{ row.item.stock_on_hand | number_format }}</template>
                    <span class="text-muted" v-else>(none)</span>
                </template>
                <template slot="actions" slot-scope="row">
                    <b-button variant="primary btn--icon btn-sm" v-if="row.item.id in updatedFertiliserValues"
                              :disabled="disabledFertiliserValues.includes(row.item.id)"
                              @click="saveFertiliserRow(row.item)">
                        <i class="zmdi zmdi-save"></i>
                    </b-button>
                    <b-button variant="warning btn--icon btn-sm" @click="enableEditFertiliserRow(row.item)" v-else>
                        <i class="zmdi zmdi-edit"></i>
                    </b-button>
                </template>
            </b-table>
        </div>
    </div>
</template>
<script>

import updateFertiliserStockOnHandMutation from '@views/StockManagement/StockOnHandManager/mutationUpdateFertiliserStockOnHand.graphql';
import updateStockOnHandMutation from '@views/StockManagement/StockOnHandManager/mutationUpdateStockOnHand.graphql';
import listLinkedChemicalsQuery from '@views/StockManagement/StockOnHandManager/queryListGrowerLinkedChemicalCommercialVariants.graphql';
import listLinkedFertilisersQuery from '@views/StockManagement/StockOnHandManager/queryListGrowerLinkedFertilisers.graphql';

/**
 * @property {String} searchTerm
 */
export default {
  name: 'StockOnHandManager',
  apollo: {
    linkedChemicals: {
      query: listLinkedChemicalsQuery,
      update: data => data.filterGrowerChemicalCommercialVariantLinks,
    },
    linkedFertilisers: {
      query: listLinkedFertilisersQuery,
      update: data => data.filterGrowerFertiliserLinks,
    },
  },
  computed: {
    currentDate() {
      return new Date();
    },
    table() {
      return {
        fields: [
          {
            key: 'name',
            label: 'Commercial Variant',
            sortable: true,
          },
          {
            key: 'activeIngredients',
            label: 'Active Ingredients',
          },
          {
            key: 'classification',
            label: 'Classification',
          },
          {
            key: 'un_number',
            label: 'UN Number',
            sortable: true,
          },
          {
            key: 'stock_on_hand',
            label: 'Stock On Hand (kg/lt)',
            sortable: true,
          },
          {
            key: 'hsr_number',
            label: 'HSR number',
            sortable: true,
          },
          {
            key: 'actions',
            label: ' ',
            class: 'd-print-none',
            sortable: false,
          },
        ],
      };
    },
    fertiliserTable() {
      return {
        fields: [
          {
            key: 'name',
            label: 'Fertiliser',
            sortable: true,
          },
          {
            key: 'type',
            label: 'Type',
          },
          {
            key: 'stock_on_hand',
            label: 'Stock On Hand (kg/lt)',
            sortable: true,
          },
          {
            key: 'actions',
            label: ' ',
            class: 'd-print-none',
            sortable: false,
          },
        ],
      };
    },
    filteredData() {
      const subjectMatchesSearch = subject => {
        return (
          (this.onlySelected === false || (('stock_on_hand' in subject) && subject.stock_on_hand)) &&
          (this.searchTerm.length === 0 || subject.name.toLowerCase().includes(this.searchTerm.toLowerCase()))
        );
      };

      const data = [];

      (this.linkedChemicals || []).forEach(chemical => {
        if (subjectMatchesSearch(chemical)) {
          data.push(chemical);
        }
      });

      return data.sort((a, b) => a.name.localeCompare(b.name));
    },
    filteredFertiliserData() {
      const subjectMatchesSearch = subject => {
        return (
          (this.onlySelected === false || (('stock_on_hand' in subject) && subject.stock_on_hand)) &&
          (this.searchTerm.length === 0 || subject.name.toLowerCase().includes(this.searchTerm.toLowerCase()))
        );
      };

      const data = [];

      (this.linkedFertilisers || []).forEach(fertiliser => {
        if (subjectMatchesSearch(fertiliser)) {
          data.push(fertiliser);
        }
      });

      return data.sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  created() {
    window.matchMedia('print').addListener(this.setIsPrint);
  },
  beforeDestroy() {
    window.matchMedia('print').removeListener(this.setIsPrint);
  },
  data() {
    return {
      linkedChemicals: [],
      linkedFertilisers: [],
      updatedValues: {},
      updatedFertiliserValues: {},
      disabledValues: [],
      disabledFertiliserValues: [],
      searchEnabled: false,
      searchTerm: '',
      onlySelected: true,
      loading: 0,
      isPrint: false,
    };
  },
  methods: {
    setIsPrint(mql) {
      this.isPrint = typeof mql === 'object' ? mql.matches : mql;
    },

    print() {
      this.setIsPrint(true);
      this.$nextTick(() => {
        window.print();
      });
    },

    enableEditRow(item) {

      this.setRow(item, item.stock_on_hand);
      this.$nextTick(() => {
        this.$refs[`stockInput${item.id}`].$el.focus();
        this.$refs[`stockInput${item.id}`].$el.select();
        // this.$refs[`stockInput${item.id}`];
      });
    },
    enableEditFertiliserRow(item) {

      this.setFertiliserRow(item, item.stock_on_hand);
      this.$nextTick(() => {
        this.$refs[`stockInputFertiliser${item.id}`].$el.focus();
        this.$refs[`stockInputFertiliser${item.id}`].$el.select();
        // this.$refs[`stockInput${item.id}`];
      });
    },
    setRow(item, value) {
      this.updatedValues = { ...this.updatedValues, [item.id]: value };
    },
    setFertiliserRow(item, value) {
      this.updatedFertiliserValues = { ...this.updatedFertiliserValues, [item.id]: value };
    },
    saveRow(item) {
      if (this.disabledValues.includes(item.id)) {
        return false;
      }

      this.disabledValues.push(item.id);
      this.disabledValues = this.disabledValues.filter(value => value !== item.id);
      this.$apollo
        .mutate({
          mutation: updateStockOnHandMutation,
          variables: {
            input: {
              id: item.id,
              stock_on_hand: this.updatedValues[item.id],
            },
          },
          optimisticResponse: {
            __typename: 'Mutation',
            updateStockOnHand: {
              __typename: 'ChemicalCommercialVariant',
              id: item.id,
              stock_on_hand: this.updatedValues[item.id],
            },
          },
        })
        .then(() => {
          this.$delete(this.updatedValues, item.id);
          this.disabledValues = this.disabledValues.filter(value => value !== item.id);
          this.$notifySuccess('Stock on hand updated!');
        })
        .catch(err => {
          console.error(err);
          this.$notifyFailure('Unfortunately, we were unable to do that!');
        });
    },
    saveFertiliserRow(item) {
      if (this.disabledFertiliserValues.includes(item.id)) {
        return false;
      }

      this.disabledFertiliserValues.push(item.id);
      this.disabledFertiliserValues = this.disabledFertiliserValues.filter(value => value !== item.id);
      this.$apollo
        .mutate({
          mutation: updateFertiliserStockOnHandMutation,
          variables: {
            input: {
              id: item.id,
              stock_on_hand: this.updatedFertiliserValues[item.id],
            },
          },
          optimisticResponse: {
            __typename: 'Mutation',
            updateFertiliserStockOnHand: {
              __typename: 'Fertiliser',
              id: item.id,
              stock_on_hand: this.updatedFertiliserValues[item.id],
            },
          },
        })
        .then(() => {
          this.$delete(this.updatedFertiliserValues, item.id);
          this.disabledFertiliserValues = this.disabledFertiliserValues.filter(value => value !== item.id);
          this.$notifySuccess('Stock on hand updated!');
        })
        .catch(err => {
          console.error(err);
          this.$notifyFailure('Unfortunately, we were unable to do that!');
        });
    },
    enableSearch() {
      this.searchEnabled = true;
      this.$nextTick(() => this.$refs.searchInput.focus());
    },
    disableSearch() {
      this.searchTerm = '';
      this.searchEnabled = false;
    },
  },
};
</script>
